// src/Pages/ContactPage.js
import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const headerRef = useRef(null);
  const formRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, amount: 0.3 });
  const formInView = useInView(formRef, { once: true, amount: 0.2 });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Prepare email content
      const emailHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
        </head>
        <body style="font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0a0f23;margin:0;padding:20px;">
          <h2 style="color:#0a0f23;margin-bottom:16px;">Contact Form Submission from cenactresolver</h2>
          <p><strong>Name:</strong> ${form.name}</p>
          <p><strong>Email:</strong> ${form.email}</p>
          <p><strong>Message:</strong></p>
          <div style="background:#f6f6f6;padding:12px;border-radius:8px;margin-top:8px;white-space:pre-wrap;">${form.message}</div>
          <p style="margin-top:16px;color:#666;font-size:12px;">Submitted on: ${new Date().toISOString()}</p>
        </body>
        </html>
      `;

      // Send email via API
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: ['cenactresolver1@gmail.com', 'xxxxtentac64@gmail.com'],
          subject: `Contact Form: ${form.name} - ${form.email}`,
          html: emailHtml,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      alert('Thank you! Your message has been sent. We will get back to you shortly.');
      setForm({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      alert(`Error: ${error.message}. Please try again or contact us directly.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1b130a] text-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="mb-6 sm:mb-8"
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-[#fde68a] via-[#a16207] to-white bg-clip-text text-transparent">
            Contact Us
          </h1>
          <motion.p 
            className="text-gray-300 text-sm sm:text-base"
            initial={{ opacity: 0 }}
            animate={headerInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Have a question or need help? Send us a message and we'll respond promptly.
          </motion.p>
        </motion.div>
        
        <motion.form 
          ref={formRef}
          onSubmit={onSubmit} 
          className="space-y-4 sm:space-y-6 bg-[#1a1209]/80 p-4 sm:p-6 md:p-8 rounded-xl border border-[#4a2f17]/40 shadow-[0_8px_20px_rgba(0,0,0,0.15)]"
          initial={{ opacity: 0, y: 40 }}
          animate={formInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={formInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <label className="block text-sm text-gray-300 mb-2">Name</label>
            <input 
              name="name" 
              value={form.name} 
              onChange={onChange} 
              required 
              className="w-full p-3 sm:p-4 rounded-lg bg-[#20160d] border border-[#4a2f17]/40 text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#a16207] transition-all text-sm sm:text-base" 
              placeholder="Your name" 
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={formInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <label className="block text-sm text-gray-300 mb-2">Email</label>
            <input 
              type="email" 
              name="email" 
              value={form.email} 
              onChange={onChange} 
              required 
              className="w-full p-3 sm:p-4 rounded-lg bg-[#20160d] border border-[#4a2f17]/40 text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#a16207] transition-all text-sm sm:text-base" 
              placeholder="you@example.com" 
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={formInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <label className="block text-sm text-gray-300 mb-2">Message</label>
            <textarea 
              name="message" 
              value={form.message} 
              onChange={onChange} 
              required 
              rows={6} 
              className="w-full p-3 sm:p-4 rounded-lg bg-[#20160d] border border-[#4a2f17]/40 text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#a16207] transition-all resize-none text-sm sm:text-base" 
              placeholder="How can we help?" 
            />
          </motion.div>
          
          <motion.div 
            className="flex justify-end"
            initial={{ opacity: 0, y: 20 }}
            animate={formInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <motion.button 
              type="submit" 
              disabled={submitting} 
              className="px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg bg-gradient-to-r from-[#92400e] to-[#b45309] text-white font-semibold hover:from-[#a16207] hover:to-[#c2410c] disabled:opacity-60 text-sm sm:text-base transition-colors"
              whileHover={{ scale: submitting ? 1 : 1.05 }}
              whileTap={{ scale: submitting ? 1 : 0.95 }}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </motion.button>
          </motion.div>
        </motion.form>
      </div>
    </div>
  );
};

export default ContactPage;


